import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import { ClickAwayListener, IconButton, InputAdornment, Popover, Stack, TextField } from "@mui/material";
import type { DateValidationError } from "@mui/x-date-pickers-pro";
import { DateCalendar } from "@mui/x-date-pickers-pro";
import type { Locale } from "date-fns";
import { format, isValid } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Maybe } from "../../../types.ts";
import { getUserLocaleInfo } from "../../../utilities/userLocaleConfig";
import {
  getDisplayFormatForLocale,
  getPlaceholderForLocale,
  normalizeDateInput,
  parseIsoOrLocalFormat,
  validateDateRange,
} from "./bcDatePickerHelper.ts";

interface Props {
  value: Date | null;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  placeholder?: string;
  fullWidth?: boolean;
  autoFocus?: boolean;
  size?: "small" | "medium";
  helperText?: string;
  onChange: (date: Date | null, validationError: DateValidationError | null) => void;
  onValidationChanged?: (error: Maybe<string>) => void;
}

const getTimestamp = (date: Date | null | undefined): number | null => (date && isValid(date) ? date.getTime() : null);

const formatDateValue = (date: Date | null, displayFormat: string, locale: Locale): string =>
  date && isValid(date) ? format(date, displayFormat, { locale }) : "";

const BcDatePicker = ({
  value,
  onChange,
  onValidationChanged,
  label,
  required = false,
  disabled = false,
  minDate,
  maxDate,
  placeholder: placeholderProp,
  fullWidth = true,
  autoFocus,
  size = "medium",
  helperText,
}: Props) => {
  const { locale, displayFormat, defaultPlaceholder } = useMemo(() => {
    const { locale } = getUserLocaleInfo();
    return {
      locale,
      displayFormat: getDisplayFormatForLocale(locale),
      defaultPlaceholder: getPlaceholderForLocale(locale),
    };
  }, []);

  const [inputValue, setInputValue] = useState(() => formatDateValue(value, displayFormat, locale));
  const [validationError, setValidationError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLInputElement | null>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const prevTimestampsRef = useRef({
    value: getTimestamp(value),
    minDate: getTimestamp(minDate),
    maxDate: getTimestamp(maxDate),
  });

  const placeholder = placeholderProp ?? defaultPlaceholder;

  const formatValue = useCallback(
    (date: Date | null) => formatDateValue(date, displayFormat, locale),
    [displayFormat, locale]
  );

  const setValidation = useCallback(
    (error: string | null) => {
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setValidationError(error);
      onValidationChanged?.(error);
    },
    [onValidationChanged]
  );

  // Sync input from props and validate range
  useEffect(() => {
    const currentTs = getTimestamp(value);
    const currentMinTs = getTimestamp(minDate);
    const currentMaxTs = getTimestamp(maxDate);

    const valueChanged = currentTs !== prevTimestampsRef.current.value;
    const minDateChanged = currentMinTs !== prevTimestampsRef.current.minDate;
    const maxDateChanged = currentMaxTs !== prevTimestampsRef.current.maxDate;

    // When disabled, always sync input and clear validation
    if (disabled) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setInputValue(formatValue(value));
      setValidation(null);
      prevTimestampsRef.current = { value: currentTs, minDate: currentMinTs, maxDate: currentMaxTs };
      return;
    }

    if (!valueChanged && !minDateChanged && !maxDateChanged) {
      return;
    }

    prevTimestampsRef.current = { value: currentTs, minDate: currentMinTs, maxDate: currentMaxTs };

    if (valueChanged) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setInputValue(formatValue(value));
    }

    if (value && isValid(value)) {
      const rangeError = validateDateRange(value, minDate, maxDate);
      setValidation(rangeError ? (rangeError === "minDate" ? "Date is too early" : "Date is too late") : null);
    } else if (valueChanged) {
      setValidation(null);
    }
  }, [disabled, value, minDate, maxDate, formatValue, setValidation]);

  const validateAndUpdate = (dateStr: string) => {
    if (dateStr.trim() === "") {
      setValidation(required ? "Field is required" : null);
      onChange(null, null);
      return;
    }
    const normalizedDateInput = normalizeDateInput(dateStr, locale);
    const parsedDate = parseIsoOrLocalFormat(normalizedDateInput || dateStr);
    if (!parsedDate) {
      // Revert input to the current valid value so UI always matches parent state
      setInputValue(formatValue(value));
      setValidation(null);
      return;
    }

    // Always format the input when the date is valid
    setInputValue(formatValue(parsedDate));

    const rangeError = validateDateRange(parsedDate, minDate, maxDate);
    setValidation(rangeError ? (rangeError === "minDate" ? "Date is too early" : "Date is too late") : null);
    onChange(parsedDate, rangeError);
  };

  const handleCalendarDateChange = (newDate: Date | null, state: "partial" | "shallow" | "finish" | undefined) => {
    if (newDate && isValid(newDate)) {
      setInputValue(formatValue(newDate));
      const rangeError = validateDateRange(newDate, minDate, maxDate);
      setValidation(rangeError ? (rangeError === "minDate" ? "Date is too early" : "Date is too late") : null);
      onChange(newDate, rangeError);
    }
    if (state === "finish") {
      setIsCalendarOpen(false);
      setAnchorEl(null);
    }
  };

  return (
    <ClickAwayListener
      onClickAway={(event) => {
        if (document.querySelector(".MuiDateCalendar-root")?.contains(event.target as Node)) return;
        setIsCalendarOpen(false);
        setAnchorEl(null);
      }}
    >
      <Stack
        direction="row"
        sx={{
          flex: 1,
        }}
      >
        <TextField
          ref={inputRef}
          fullWidth={fullWidth}
          autoFocus={autoFocus}
          size={size}
          label={label}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onBlur={() => validateAndUpdate(inputValue)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              validateAndUpdate(inputValue);
              inputRef.current?.blur();
            }
          }}
          disabled={disabled}
          error={!!validationError}
          helperText={helperText}
          placeholder={placeholder}
          sx={{
            "& .MuiOutlinedInput-root.Mui-disabled.Mui-error .MuiOutlinedInput-notchedOutline": {
              borderColor: "error.main",
            },
          }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={() => {
                      if (!disabled) {
                        setAnchorEl(inputRef.current);
                        setIsCalendarOpen(true);
                      }
                    }}
                    disabled={disabled}
                    size="small"
                  >
                    <CalendarTodayRoundedIcon />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        <Popover
          open={isCalendarOpen}
          anchorEl={anchorEl}
          onClose={() => {
            setIsCalendarOpen(false);
            setAnchorEl(null);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{ paper: { sx: { zIndex: 1400 } } }}
        >
          <DateCalendar value={value} onChange={handleCalendarDateChange} minDate={minDate} maxDate={maxDate} />
        </Popover>
      </Stack>
    </ClickAwayListener>
  );
};

export default BcDatePicker;
